/**
 * Progress Service
 *
 * Handles project progress tracking including photo uploads,
 * progress reports, and timeline generation.
 *
 * @module services/progressService
 */

import { supabase } from './supabase';
import { STORAGE_BUCKETS } from '../utils/constants';

/**
 * Extract the internal storage object path from a Supabase Storage URL.
 * Handles the `sign` / `public` / `authenticated` URL shapes; returns the
 * input unchanged if it's already a bare path, or null if it's some other
 * (external / unrecognized) URL we can't resolve a path from.
 *
 * Used by the read-time re-signing helpers below to recover a path for
 * legacy rows that persisted a full URL instead of a path (pre-#332).
 * @param {string} bucket - Storage bucket name
 * @param {string} url - URL or path
 * @returns {string|null}
 */
const extractStoragePath = (bucket, url) => {
  if (!url) return null;
  if (!/^https?:\/\//i.test(url)) return url; // already a bare path
  const match = url.match(new RegExp(`/storage/v1/object/(?:sign|public|authenticated)/${bucket}/([^?]+)`));
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

/**
 * Resolve a fresh, working `photo_url` for a progress_photos row.
 *
 * #332: `progress_photos.photo_url` no longer stores a persisted signed URL
 * (those expire in 1h — see CLAUDE.md storage policy — and a persisted one
 * goes dead, silently rotting the evidence). The DB stores the stable
 * object path; every read re-signs on demand here, so a photo uploaded
 * months ago still resolves. Best-effort: if no path can be determined
 * (very old rows with an external/unrecognized URL and no photo_path), the
 * stored value is returned unchanged rather than failing the read.
 * @param {Object} photo - progress_photos row (or null)
 * @returns {Promise<Object>} the same row with photo_url refreshed
 */
export const resolvePhotoUrl = async (photo) => {
  if (!photo) return photo;
  const path = photo.photo_path || extractStoragePath(STORAGE_BUCKETS.PROGRESS_PHOTOS, photo.photo_url);
  if (!path) return photo;

  const { data, error } = await supabase.storage
    .from(STORAGE_BUCKETS.PROGRESS_PHOTOS)
    .createSignedUrl(path, 3600); // 1-hour expiry (see CLAUDE.md storage policy) — freshly minted on every read

  if (error || !data?.signedUrl) {
    console.warn('Failed to re-sign progress photo URL, returning stored value:', error);
    return photo;
  }

  return { ...photo, photo_url: data.signedUrl };
};

/**
 * Resolve fresh `photo_url`s for an array of progress_photos rows. See
 * {@link resolvePhotoUrl}.
 * @param {Array<Object>} photos
 * @returns {Promise<Array<Object>>}
 */
export const resolvePhotoUrls = async (photos) => {
  if (!photos?.length) return photos;
  return Promise.all(photos.map(resolvePhotoUrl));
};

/**
 * Upload progress photo to Supabase Storage
 * @param {File} file - Photo file
 * @param {string} projectId - Project ID for organizing files
 * @returns {Promise<{path, error}>}
 */
const uploadProgressPhoto = async (file, projectId) => {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${projectId}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKETS.PROGRESS_PHOTOS)
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) throw error;

    // #332: do NOT persist a signed (or public) URL here. `progress-photos`
    // is a private bucket by design (technician-uploaded client-site
    // evidence; RLS scopes reads to staff + the project's own client
    // contacts — see the storage policies in the baseline migration). A
    // signed URL is a credential with an expiry, not a stable identifier;
    // persisting one means the evidence rots on its own a fixed time after
    // upload. Only the stable object path is returned/stored — callers
    // re-sign via resolvePhotoUrl/resolvePhotoUrls at read time.
    return { path: data.path, error: null };
  } catch (error) {
    console.error('Upload progress photo error:', error);
    return { path: null, error };
  }
};

/**
 * Upload multiple progress photos with metadata
 * @param {string} projectId - Project ID
 * @param {Array<File>} files - Array of photo files
 * @param {string} caption - Photo caption/description (optional)
 * @param {Object} gpsCoords - GPS coordinates (optional)
 * @param {number} gpsCoords.latitude - Latitude
 * @param {number} gpsCoords.longitude - Longitude
 * @param {string} taskId - Task ID for task-specific photos (optional)
 * @returns {Promise<{data, errors, error}>}
 */
export const uploadProgressPhotos = async (
  projectId,
  files,
  caption = null,
  gpsCoords = null,
  taskId = null
) => {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const results = {
      success: [],
      errors: [],
    };

    // Upload each file
    for (const file of files) {
      const uploadResult = await uploadProgressPhoto(file, projectId);

      if (uploadResult.error) {
        results.errors.push({
          fileName: file.name,
          error: uploadResult.error.message,
        });
        continue;
      }

      // Insert photo record.
      // GPS columns are gps_latitude / gps_longitude (renamed from latitude/longitude
      // in migration 013_task_attachments.sql). Persist them when the caller provides
      // coordinates (the "Incluir ubicación GPS" toggle in /progreso).
      //
      // #332: photo_url stores the object PATH, not a signed URL (NOT NULL
      // constraint on the column means we still need a value there — the
      // path doubles as it; it's never rendered directly, only resolved via
      // resolvePhotoUrl at read time).
      const { data, error } = await supabase
        .from('progress_photos')
        .insert({
          project_id: projectId,
          task_id: taskId || null,
          photo_url: uploadResult.path,
          photo_path: uploadResult.path,
          caption,
          gps_latitude: gpsCoords?.latitude ?? null,
          gps_longitude: gpsCoords?.longitude ?? null,
          uploaded_by: user.id,
        })
        .select(`
          *,
          project:projects(id, project_number, title),
          task:tasks(id, task_number, title),
          uploaded_by_profile:profiles!uploaded_by(id, full_name)
        `)
        .single();

      if (error) {
        console.error('Database insert error for photo:', {
          fileName: file.name,
          error: error,
          code: error.code,
          details: error.details,
          hint: error.hint,
          message: error.message,
        });
        results.errors.push({
          fileName: file.name,
          error: error.message,
          code: error.code,
          details: error.details,
        });
        // Clean up uploaded file
        await supabase.storage
          .from(STORAGE_BUCKETS.PROGRESS_PHOTOS)
          .remove([uploadResult.path]);
      } else {
        // Resolve a working signed URL for the immediate UI response only —
        // the stored row keeps the stable path (see comment above).
        const resolved = await resolvePhotoUrl(data);
        console.log('Photo saved successfully:', {
          fileName: file.name,
          photoId: resolved.id,
          photoUrl: resolved.photo_url,
        });
        results.success.push(resolved);
      }
    }

    return {
      data: results.success,
      errors: results.errors,
      error: null,
    };
  } catch (error) {
    console.error('Upload progress photos error:', error);
    return { data: null, errors: null, error };
  }
};

/**
 * Get progress photos for a project
 * @param {string} projectId - Project ID
 * @param {Object} options - Query options
 * @param {number} options.limit - Limit number of results
 * @param {number} options.offset - Offset for pagination
 * @param {string} options.orderBy - Order by field (default: 'uploaded_at')
 * @param {boolean} options.ascending - Sort order (default: false)
 * @returns {Promise<{data, count, error}>}
 */
export const getProgressPhotos = async (projectId, options = {}) => {
  try {
    const {
      limit = 20,
      offset = 0,
      orderBy = 'uploaded_at',
      ascending = false,
    } = options;

    let query = supabase
      .from('progress_photos')
      .select(`
        *,
        project:projects(id, project_number, title),
        uploaded_by_profile:profiles!uploaded_by(id, full_name)
      `, { count: 'exact' })
      .eq('project_id', projectId);

    query = query
      .order(orderBy, { ascending })
      .range(offset, offset + limit - 1);

    const { data, count, error } = await query;

    if (error) throw error;

    // #332: photo_url is stored as a path, not a live URL — re-sign on read.
    const resolved = await resolvePhotoUrls(data);

    return { data: resolved, count, error: null };
  } catch (error) {
    console.error('Get progress photos error:', error);
    return { data: null, count: 0, error };
  }
};

/**
 * Get single progress photo by ID
 * @param {string} photoId - Photo ID
 * @returns {Promise<{data, error}>}
 */
export const getProgressPhoto = async (photoId) => {
  try {
    const { data, error } = await supabase
      .from('progress_photos')
      .select(`
        *,
        project:projects(id, project_number, title),
        uploaded_by_profile:profiles!uploaded_by(id, full_name)
      `)
      .eq('id', photoId)
      .single();

    if (error) throw error;

    // #332: re-sign on read (see resolvePhotoUrl).
    const resolved = await resolvePhotoUrl(data);

    return { data: resolved, error: null };
  } catch (error) {
    console.error('Get progress photo error:', error);
    return { data: null, error };
  }
};

/**
 * Update progress photo caption
 * @param {string} photoId - Photo ID
 * @param {string} caption - New caption
 * @returns {Promise<{data, error}>}
 */
export const updateProgressPhotoCaption = async (photoId, caption) => {
  try {
    const { data, error } = await supabase
      .from('progress_photos')
      .update({ caption })
      .eq('id', photoId)
      .select(`
        *,
        project:projects(id, project_number, title),
        uploaded_by_profile:profiles!uploaded_by(id, full_name)
      `)
      .single();

    if (error) throw error;

    // #332: re-sign on read (see resolvePhotoUrl).
    const resolved = await resolvePhotoUrl(data);

    return { data: resolved, error: null };
  } catch (error) {
    console.error('Update progress photo caption error:', error);
    return { data: null, error };
  }
};

/**
 * Delete progress photo
 * @param {string} photoId - Photo ID
 * @returns {Promise<{success, error}>}
 */
export const deleteProgressPhoto = async (photoId) => {
  try {
    // Get photo to delete from storage
    const { data: photo, error: fetchError } = await getProgressPhoto(photoId);
    if (fetchError) throw fetchError;

    // ORDEN: PRIMERO LA FILA, DESPUÉS EL ARCHIVO.
    //
    // Acá el archivo se borraba ANTES de saber si la base aceptaba borrar la
    // fila — y una RLS que rechaza un DELETE no devuelve error, devuelve CERO
    // filas (#529). O sea que el camino "no tenés permiso" destruía la foto,
    // dejaba la fila viva apuntando a un archivo inexistente y devolvía
    // `success: true`. Una fila sin su archivo es peor que un archivo sin su
    // fila: el huérfano se recupera a mano, la evidencia borrada no.
    //
    // Paridad con el carril MCP (`mcp-server/src/tools/parityTools.js`,
    // `deleteProgressPhoto`), que hace exactamente esta secuencia.
    const { data: removed, error } = await supabase
      .from('progress_photos')
      .delete()
      .eq('id', photoId)
      .select('id');

    if (error) throw error;

    if (!removed || removed.length === 0) {
      // No se borró nada y el archivo sigue intacto, que es lo correcto.
      return {
        success: false,
        error: new Error(
          'No se borró nada: no tenés permiso para eliminar esa foto. El registro y el archivo siguen ahí.'
        ),
      };
    }

    // La fila ya no está. Si Storage falla, el archivo queda huérfano: se
    // reporta, NO se revierte el borrado en silencio (mismo criterio que
    // `deactivateUser` en el carril MCP con la revocación de sesiones).
    let fileRemoved = false;
    if (photo.photo_path) {
      const { error: storageError } = await supabase.storage
        .from(STORAGE_BUCKETS.PROGRESS_PHOTOS)
        .remove([photo.photo_path]);

      if (storageError) {
        console.error(
          'Progress photo row deleted but the file survives in Storage:',
          photo.photo_path,
          storageError
        );
      } else {
        fileRemoved = true;
      }
    }

    return { success: true, fileRemoved, error: null };
  } catch (error) {
    console.error('Delete progress photo error:', error);
    return { success: false, error };
  }
};

/**
 * Create progress report
 * @param {string} projectId - Project ID
 * @param {Object} reportData - Report data
 * @param {string} reportData.title - Report title
 * @param {string} reportData.description - Report description
 * @param {string} reportData.status - Current status
 * @param {number} reportData.completion_percentage - Completion percentage (0-100)
 * @param {Array<string>} reportData.photo_ids - Array of photo IDs to include
 * @returns {Promise<{data, error}>}
 */
export const createProgressReport = async (projectId, reportData) => {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { data, error } = await supabase
      .from('progress_reports')
      .insert({
        project_id: projectId,
        ...reportData,
        created_by: user.id,
      })
      .select(`
        *,
        project:projects(id, project_number, title),
        created_by_profile:profiles!created_by(id, full_name)
      `)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Create progress report error:', error);
    return { data: null, error };
  }
};

/**
 * Get project timeline for a project
 * Combines all project events into a chronological timeline
 * @param {string} projectId - Project ID
 * @param {Object} options - Query options
 * @param {number} options.limit - Limit number of results
 * @returns {Promise<{data, error}>}
 */
export const getProjectTimeline = async (projectId, options = {}) => {
  try {
    const { limit = 50 } = options;

    // Get project creation event
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .select(`
        id,
        project_number,
        created_at,
        created_by_profile:profiles!created_by(full_name)
      `)
      .eq('id', projectId)
      .single();

    if (projectError) throw projectError;

    // Get progress photos
    const { data: rawPhotos, error: photosError } = await supabase
      .from('progress_photos')
      .select(`
        id,
        photo_url,
        photo_path,
        caption,
        uploaded_at,
        uploaded_by_profile:profiles!uploaded_by(full_name)
      `)
      .eq('project_id', projectId);

    if (photosError) throw photosError;

    // #332: re-sign on read (see resolvePhotoUrl).
    const photos = await resolvePhotoUrls(rawPhotos);

    // Get expenses
    const { data: expenses, error: expensesError } = await supabase
      .from('expenses')
      .select(`
        id,
        category,
        amount,
        vendor,
        expense_date,
        created_by_profile:profiles!created_by(full_name)
      `)
      .eq('project_id', projectId);

    if (expensesError) throw expensesError;

    // Get quotations
    const { data: quotations, error: quotationsError } = await supabase
      .from('quotations')
      .select(`
        id,
        quotation_number,
        status,
        total,
        created_at,
        approved_at,
        created_by_profile:profiles!created_by(full_name)
      `)
      .eq('project_id', projectId);

    if (quotationsError) throw quotationsError;

    // Build timeline events
    const timeline = [];

    // Add project creation event
    if (project) {
      timeline.push({
        id: `project-created-${project.id}`,
        type: 'project_created',
        date: project.created_at,
        title: 'Proyecto Creado',
        description: `Proyecto ${project.project_number} iniciado`,
        user: project.created_by_profile?.full_name,
        data: project,
      });
    }

    // Add quotation events
    quotations.forEach(quotation => {
      // Quotation sent
      timeline.push({
        id: `quotation-sent-${quotation.id}`,
        type: 'quotation_sent',
        date: quotation.created_at,
        title: 'Cotización Enviada',
        description: `Cotización ${quotation.quotation_number}`,
        user: quotation.created_by_profile?.full_name,
        data: quotation,
      });

      // Quotation approved
      if (quotation.approved_at) {
        timeline.push({
          id: `quotation-approved-${quotation.id}`,
          type: 'quotation_approved',
          date: quotation.approved_at,
          title: 'Cotización Aprobada',
          description: `Cotización ${quotation.quotation_number} aprobada`,
          user: quotation.created_by_profile?.full_name,
          data: quotation,
        });
      }
    });

    // Add expense events
    expenses.forEach(expense => {
      timeline.push({
        id: `expense-${expense.id}`,
        type: 'expense_recorded',
        date: expense.expense_date,
        title: 'Gasto Registrado',
        description: `${expense.category} - ${expense.vendor || 'Sin proveedor'}`,
        user: expense.created_by_profile?.full_name,
        data: expense,
      });
    });

    // Add progress photo events
    photos.forEach(photo => {
      timeline.push({
        id: `photo-${photo.id}`,
        type: 'progress_photo_uploaded',
        date: photo.uploaded_at,
        title: 'Foto de Progreso',
        description: photo.caption || 'Sin descripción',
        user: photo.uploaded_by_profile?.full_name,
        data: photo,
      });
    });

    // Sort by date (most recent first)
    timeline.sort((a, b) => new Date(b.date) - new Date(a.date));

    // Limit results
    const limitedTimeline = limit ? timeline.slice(0, limit) : timeline;

    return { data: limitedTimeline, error: null };
  } catch (error) {
    console.error('Get project timeline error:', error);
    return { data: null, error };
  }
};

/**
 * @deprecated Use getProjectTimeline instead
 */
export const getProgressTimeline = getProjectTimeline;

/**
 * Get signed URL for progress photo
 * @param {string} photoPath - Photo file path
 * @param {number} expiresIn - URL expiration time in seconds (default 3600)
 * @returns {Promise<{url, error}>}
 */
export const getProgressPhotoSignedUrl = async (photoPath, expiresIn = 3600) => {
  try {
    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKETS.PROGRESS_PHOTOS)
      .createSignedUrl(photoPath, expiresIn);

    if (error) throw error;

    return { url: data.signedUrl, error: null };
  } catch (error) {
    console.error('Get progress photo signed URL error:', error);
    return { url: null, error };
  }
};

/**
 * Get project progress statistics
 * @param {string} projectId - Project ID
 * @returns {Promise<{data, error}>}
 */
export const getProgressStatistics = async (projectId) => {
  try {
    const { data: photos, error: photosError } = await supabase
      .from('progress_photos')
      .select('id, uploaded_at')
      .eq('project_id', projectId);

    if (photosError) throw photosError;

    const totalPhotos = photos.length;
    const firstPhotoDate = photos.length > 0
      ? new Date(Math.min(...photos.map(p => new Date(p.uploaded_at))))
      : null;
    const lastPhotoDate = photos.length > 0
      ? new Date(Math.max(...photos.map(p => new Date(p.uploaded_at))))
      : null;

    // Get days since last update
    const daysSinceLastUpdate = lastPhotoDate
      ? Math.floor((new Date() - lastPhotoDate) / (1000 * 60 * 60 * 24))
      : null;

    return {
      data: {
        totalPhotos,
        firstPhotoDate,
        lastPhotoDate,
        daysSinceLastUpdate,
      },
      error: null,
    };
  } catch (error) {
    console.error('Get progress statistics error:', error);
    return { data: null, error };
  }
};

/**
 * Get task photos
 * @param {string} taskId - Task ID
 * @param {Object} options - Query options
 * @returns {Promise<{data, count, error}>}
 */
export const getTaskPhotos = async (taskId, options = {}) => {
  try {
    const {
      limit = 20,
      offset = 0,
      orderBy = 'uploaded_at',
      ascending = false,
    } = options;

    // Simplified query - FK joins may fail if constraints don't exist
    let query = supabase
      .from('progress_photos')
      .select('*', { count: 'exact' })
      .eq('task_id', taskId);

    query = query
      .order(orderBy, { ascending })
      .range(offset, offset + limit - 1);

    const { data, count, error } = await query;

    if (error) throw error;

    // #332: photo_url is stored as a path, not a live URL — re-sign on read.
    const resolved = await resolvePhotoUrls(data);

    return { data: resolved, count, error: null };
  } catch (error) {
    console.error('Get task photos error:', error);
    return { data: null, count: 0, error };
  }
};

/**
 * Upload task photos (convenience wrapper)
 * @param {string} taskId - Task ID
 * @param {string} projectId - Project ID
 * @param {Array<File>} files - Array of photo files
 * @param {string} caption - Photo caption/description (optional)
 * @param {Object} gpsCoords - GPS coordinates (optional)
 * @returns {Promise<{data, errors, error}>}
 */
export const uploadTaskPhotos = async (
  taskId,
  projectId,
  files,
  caption = null,
  gpsCoords = null
) => {
  return uploadProgressPhotos(projectId, files, caption, gpsCoords, taskId);
};

const progressService = {
  uploadProgressPhotos,
  uploadTaskPhotos,
  getProgressPhotos,
  getTaskPhotos,
  getProgressPhoto,
  updateProgressPhotoCaption,
  deleteProgressPhoto,
  createProgressReport,
  getProgressTimeline,
  getProgressPhotoSignedUrl,
  getProgressStatistics,
};

export default progressService;
